import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import {
  ResponsiveGridLayout,
  useContainerWidth,
  type Layout,
  type LayoutItem,
  type ResponsiveLayouts,
} from 'react-grid-layout';
import { MessageBar, MessageBarBody, MessageBarTitle } from '@fluentui/react-components';
import type { Chart as ApiChart } from '../../api/types';
import { ChartContainer } from '../charts/shared/ChartContainer';
import { useWorkspaceAccess } from '../security/WorkspaceAccess';
import {
  GRID_COLS,
  GRID_ROW_HEIGHT,
  GRID_MARGIN,
  GRID_CONTAINER_PADDING,
} from './types';
import './DashboardGridLayout.css';

/**
 * Memoized grid item child — prevents chart re-renders during drag/resize.
 */
const MemoizedGridItem = React.memo<{
  chart: ApiChart;
  dashboardId: string;
  filters: Record<string, any>;
  filtersReady: boolean;
  isDark: boolean;
  onEditChart?: (chartId: string) => void;
  onDuplicateChart?: (chartId: string) => void;
  onDeleteChart?: (chartId: string) => void;
  onSetAsThumbnail?: (chartId: string) => void;
  dashboardTheme?: string;
  animated?: boolean;
  recompile?: boolean;
  shareToken?: string;
  sharedDashboardId?: string;
}>(({ chart, dashboardId, filters, filtersReady, isDark, onEditChart, onDuplicateChart, onDeleteChart, onSetAsThumbnail, dashboardTheme, animated, recompile, shareToken, sharedDashboardId }) => (
  <div className="grid-item-wrapper">
    <ChartContainer
      chartType={chart.chartType || 'line'}
      chartConfig={chart}
      isDark={isDark}
      animated={animated}
      dashboardTheme={dashboardTheme}
      onEdit={onEditChart}
      onDuplicate={onDuplicateChart}
      onDelete={onDeleteChart}
      onSetAsThumbnail={onSetAsThumbnail}
      dashboardId={dashboardId}
      filters={filters}
      filtersReady={filtersReady}
      recompile={recompile}
      shareToken={shareToken}
      sharedDashboardId={sharedDashboardId}
    />
  </div>
));

MemoizedGridItem.displayName = 'MemoizedGridItem';

interface DashboardGridLayoutProps {
  dashboardId: string;
  charts: ApiChart[];
  layout: LayoutItem[];
  onLayoutChange: (newLayout: LayoutItem[]) => void;
  filters: Record<string, any>;
  filtersReady: boolean;
  isDark: boolean;
  dashboardTheme?: string;
  animated?: boolean;
  recompile?: boolean;
  shareToken?: string;
  sharedDashboardId?: string;
  onEditChart?: (chartId: string) => void;
  onDuplicateChart?: (chartId: string) => void;
  onDeleteChart?: (chartId: string) => void;
  onSetAsThumbnail?: (chartId: string) => void;
}

/**
 * Dashboard grid layout component powered by react-grid-layout v2.
 *
 * Layout state is externally managed by `useLayoutEditor`.
 * This component only handles rendering + drag/resize interactions.
 *
 * ECharts instances auto-resize via ResizeObserver (no manual reflow needed).
 */
const DashboardGridLayout: React.FC<DashboardGridLayoutProps> = ({
  dashboardId,
  charts,
  layout,
  onLayoutChange,
  filters,
  filtersReady,
  isDark,
  dashboardTheme,
  animated,
  recompile,
  shareToken,
  sharedDashboardId,
  onEditChart,
  onDuplicateChart,
  onDeleteChart,
  onSetAsThumbnail,
}) => {
  const { isReadOnly } = useWorkspaceAccess();
  const [activeGhostId, setActiveGhostId] = useState<string | null>(null);

  const { width, containerRef, mounted } = useContainerWidth({ initialWidth: 1200 });

  // ── Auto-scroll while dragging near edges ──────────────────────────────
  const mouseYRef = useRef(0);
  const rafIdRef = useRef<number | null>(null);
  const scrollContainerRef = useRef<HTMLElement | null>(null);

  const autoScrollLoop = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const EDGE_ZONE = 60; // px from top/bottom edge to trigger scroll
    const MAX_SPEED = 12; // max px/frame scroll speed

    const rect = container.getBoundingClientRect();
    const y = mouseYRef.current;

    if (y < rect.top + EDGE_ZONE) {
      // Near top edge — scroll up
      const proximity = Math.max(0, rect.top + EDGE_ZONE - y);
      const speed = Math.ceil((proximity / EDGE_ZONE) * MAX_SPEED);
      container.scrollTop -= speed;
    } else if (y > rect.bottom - EDGE_ZONE) {
      // Near bottom edge — scroll down
      const proximity = Math.max(0, y - (rect.bottom - EDGE_ZONE));
      const speed = Math.ceil((proximity / EDGE_ZONE) * MAX_SPEED);
      container.scrollTop += speed;
    }

    rafIdRef.current = requestAnimationFrame(autoScrollLoop);
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    mouseYRef.current = e.clientY;
  }, []);

  const startAutoScroll = useCallback(() => {
    // Find the scrollable .dashboard-content ancestor
    scrollContainerRef.current =
      containerRef.current?.closest('.dashboard-content') as HTMLElement | null;
    if (!scrollContainerRef.current) return;

    document.addEventListener('mousemove', handleMouseMove);
    rafIdRef.current = requestAnimationFrame(autoScrollLoop);
  }, [autoScrollLoop, handleMouseMove]);

  const stopAutoScroll = useCallback(() => {
    document.removeEventListener('mousemove', handleMouseMove);
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    scrollContainerRef.current = null;
  }, [handleMouseMove]);

  // Clean up on unmount
  useEffect(() => stopAutoScroll, [stopAutoScroll]);

  const handleDragStart = useCallback(
    (_layout: Layout, _oldItem: LayoutItem | null, newItem: LayoutItem | null) => {
      if (isReadOnly) return;
      if (newItem) setActiveGhostId(newItem.i);
      startAutoScroll();
    },
    [isReadOnly, startAutoScroll]
  );

  const handleDragStop = useCallback(
    (newLayout: Layout) => {
      setActiveGhostId(null);
      stopAutoScroll();
      if (isReadOnly) return;
      const chartIdSet = new Set(charts.map((c) => c.id));
      const filtered = newLayout
        .filter((item) => chartIdSet.has(item.i))
        .map((item) => ({ ...item }));
      onLayoutChange(filtered);
    },
    [charts, isReadOnly, onLayoutChange, stopAutoScroll]
  );

  const handleResizeStart = useCallback(
    (_layout: Layout, _oldItem: LayoutItem | null, newItem: LayoutItem | null) => {
      if (isReadOnly) return;
      if (newItem) setActiveGhostId(newItem.i);
    },
    [isReadOnly]
  );

  const handleResizeStop = useCallback(
    (newLayout: Layout) => {
      setActiveGhostId(null);
      stopAutoScroll();
      if (isReadOnly) return;
      const chartIdSet = new Set(charts.map((c) => c.id));
      const filtered = newLayout
        .filter((item) => chartIdSet.has(item.i))
        .map((item) => ({ ...item }));
      onLayoutChange(filtered);

      // ECharts instances auto-resize via ResizeObserver.
      // Dispatch a window resize event as a fallback for edge cases.
      window.dispatchEvent(new Event('resize'));
    },
    [charts, isReadOnly, onLayoutChange, stopAutoScroll]
  );

  const layouts = useMemo(
    (): ResponsiveLayouts => ({ lg: layout }),
    [layout]
  );

  const chartMap = useMemo(
    () => new Map(charts.map((c) => [c.id, c])),
    [charts]
  );

  if (!charts.length) {
    return (
      <div className="dashboard-grid-empty">
        <MessageBar intent="info">
          <MessageBarBody>
            <MessageBarTitle>No Charts</MessageBarTitle>
            This dashboard doesn't have any charts yet. Add charts to visualize your data.
          </MessageBarBody>
        </MessageBar>
      </div>
    );
  }

  return (
    <div className="dashboard-grid-layout" ref={containerRef}>
      {mounted && (
        <ResponsiveGridLayout
          width={width}
          layouts={layouts}
          breakpoints={{ lg: 900, md: 600, sm: 480, xs: 0 }}
          cols={{ lg: GRID_COLS, md: GRID_COLS, sm: 6, xs: 4 }}
          rowHeight={GRID_ROW_HEIGHT}
          margin={GRID_MARGIN}
          containerPadding={GRID_CONTAINER_PADDING}
          onDragStart={handleDragStart}
          onDragStop={handleDragStop}
          onResizeStart={handleResizeStart}
          onResizeStop={handleResizeStop}
          dragConfig={{ enabled: !isReadOnly, cancel: '.chart-actions, .react-resizable-handle' }}
          resizeConfig={{ enabled: !isReadOnly, handles: isReadOnly ? [] : ['se', 'sw'] }}
          compactor={undefined}
        >
          {layout.map((item) => {
            const chart = chartMap.get(item.i);
            if (!chart) return null;

            return (
              <div
                key={item.i}
                data-chart-id={item.i}
                className={`${!isReadOnly ? 'dashboard-grid-item-draggable' : ''} ${activeGhostId === item.i ? 'grid-item-ghost' : ''}`.trim()}
              >
                <MemoizedGridItem
                  chart={chart}
                  dashboardId={dashboardId}
                  filters={filters}
                  filtersReady={filtersReady}
                  isDark={isDark}
                  dashboardTheme={dashboardTheme}
                  animated={animated}
                  recompile={recompile}
                  shareToken={shareToken}
                  sharedDashboardId={sharedDashboardId}
                  onEditChart={onEditChart}
                  onDuplicateChart={onDuplicateChart}
                  onDeleteChart={onDeleteChart}
                  onSetAsThumbnail={onSetAsThumbnail}
                />
              </div>
            );
          })}
        </ResponsiveGridLayout>
      )}
    </div>
  );
};

export default DashboardGridLayout;